/**
 * SubNav - port 1:1 z new-design/layout.jsx.
 *
 * Contextual subnav po RailNav. Sekcje z NAV array. Active state przez routeMatches.
 * Specjalne renderowania:
 *   - galeria: dorzuca liste ostatnich 5 projektow (z API via props.projects)
 *   - konto: dorzuca subcredits box z kredytami, przycisk Doladuj i wylogowanie
 */
import { Icon } from "@/components/ui/Icon";
import { Btn } from "@/components/ui/Btn";
import { useAuth } from "@/hooks/useAuth";
import { navSection } from "@/lib/data";
import type { NavRoute } from "@/lib/data";
import { routeMatches } from "@/lib/routing";
import { formatCredits } from "@/lib/credits";
import { OfferSectionsSidebar } from "@/components/offer/OfferSectionsSidebar";

interface Project {
  id: string;
  name: string;
  archived?: boolean;
  unassigned?: boolean;
}

interface SubNavProps {
  sectionId: string;
  route: NavRoute;
  navigate: (target: NavRoute) => void;
  onCollapse: () => void;
  /** `null` = saldo jeszcze nie dojechało z API (patrz lib/credits). */
  credits: number | null;
  projects?: Project[];
  onBuy: () => void;
  /** Czy pokazywać cennik abonamentowy i „Doładuj" (patrz useCreditPurchase). */
  canBuy?: boolean;
}

export function SubNav({
  sectionId,
  route,
  navigate,
  onCollapse,
  credits,
  projects,
  onBuy,
  canBuy = true,
}: SubNavProps) {
  const { signOut } = useAuth();
  const sec = navSection(sectionId);
  if (!sec) return null;

  // Bez prawa zakupu (płatności nieodsłonięte ALBO członek biura — #56) pozycje
  // cennikowe znikają z menu w całości: link do strony „nie możesz kupić" jest
  // gorszy niż brak linku.
  const items = canBuy
    ? sec.items
    : sec.items.filter((it) => it.route.name !== "plans" && it.route.name !== "pricing");

  // Wewnątrz oferty (/oferty/:projectId) sidebar pokazuje sekcje tej oferty
  // (Przegląd, Księga wieczysta, …) zamiast listy ofert — 3. poziom nawigacji.
  // „Nie przypisane" to nie oferta, więc tam zostaje zwykła lista.
  const inOffer = route.name === "project" && !!route.projectId && route.projectId !== "unassigned";

  return (
    <aside className="subnav">
      <div className="subnav__head">
        <span className="subnav__title">{sec.label}</span>
        <button className="subnav__collapse" onClick={onCollapse} title="Zwiń panel">
          <Icon name="split" />
        </button>
      </div>

      {inOffer && <OfferSectionsSidebar projectId={route.projectId!} />}

      {!inOffer && (
      <>
      <nav className="subnav__list">
        {items.map((it, i) => {
          const active = routeMatches(route, it.route);
          return (
            <button
              key={i}
              className={`subitem ${active ? "is-active" : ""}`}
              style={active && it.accent ? { ["--tool" as string]: it.accent } : undefined}
              onClick={() => navigate(it.route)}
            >
              <Icon name={it.icon} />
              <span>{it.label}</span>
            </button>
          );
        })}
      </nav>

      {sectionId === "galeria" && projects && (
        <>
          <div className="subnav__divider" />
          <div className="subnav__label micro">Ostatnie oferty</div>
          <div className="subnav__list" style={{ paddingTop: 0 }}>
            {projects
              .filter((p) => !p.archived && !p.unassigned)
              .slice(0, 5)
              .map((p) => (
                <div
                  key={p.id}
                  className="subproj"
                  onClick={() => navigate({ name: "project", projectId: p.id })}
                >
                  <span className="subproj__dot" />
                  <span className="subproj__name">{p.name}</span>
                </div>
              ))}
          </div>
        </>
      )}
      </>
      )}

      {sectionId === "konto" && (
        <div className="subnav__foot">
          <div className="subcredits">
            <div className="micro" style={{ marginBottom: 7 }}>
              Twoje kredyty
            </div>
            <div className="subcredits__row">
              <Icon name="coins" className="credits__coin" />
              <span className="subcredits__num">{formatCredits(credits)}</span>
              <span className="muted" style={{ fontSize: 13 }}>
                kredytów
              </span>
            </div>
            {canBuy ? (
              <Btn
                variant="primary"
                size="sm"
                block
                icon="coins"
                style={{ marginTop: 11 }}
                onClick={onBuy}
              >
                Doładuj
              </Btn>
            ) : (
              <p className="muted" style={{ fontSize: 12, marginTop: 11 }}>
                Doładowania kredytów będą wkrótce.
              </p>
            )}
          </div>
          {/* Wylogowanie na poziomie sekcji „Konto”, nie w głębi zakładki „Dane
              klienta” — uwaga wspólnika 10.09: „opcja wylogowania w zupełnie
              nieintuicyjnym miejscu, trzeba wejść w dane klienta". */}
          <Btn variant="ghost" size="sm" block icon="arrowL" style={{ marginTop: 10 }} onClick={signOut}>
            Wyloguj się
          </Btn>
        </div>
      )}
    </aside>
  );
}
